import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import type { EllipseObject } from './ellipse.interface.js';

const DEFAULT_STROKE_COLOR = '#374151';
const DEFAULT_STROKE_WIDTH = 3;
const DEFAULT_FILL = '';

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the EllipseObject contract.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Stefan, 2026-09-03 — "choose properties before drawing": optional overrides
 * for the pre-placement-staged options-circle values. Mirrors
 * `CreateRectangleDefaultsOptions`.
 */
export interface CreateEllipseDefaultsOptions {
  readonly strokeColor?: string;
  readonly strokeWidth?: number;
  readonly fill?: string;
  /** Bugreport (Anke, 2026-09-03): "fehlen für: schatten, transparenz" — no
   *  hardcoded default (unset renders as "off", same as after drawing). */
  readonly boxShadow?: 'on';
  readonly backgroundOpacity?: number;
  readonly strokeStyle?: 'solid' | 'dashed' | 'dotted';
}

/**
 * Build a fresh ellipse default payload — extracted from the inline
 * `placeEllipseAt` body in `o2d-whiteboard.element.ts` per PRD-F1
 * §TR-Defaults-Factory Ellipse.
 *
 * - `sizeOpt === null`: bare-click branch, ellipse bounding box is centred
 *   on the pointer (top-left = point - size/2). Side length scales inversely
 *   with viewport scale via `creationDefaults`.
 * - `sizeOpt !== null`: drag-to-create branch, the dragged box is the exact
 *   placement rectangle (top-left = pointer).
 *
 * Note: Unlike `createRectangleDefaults`, the result has no `cornerRadius`
 * (an ellipse is intrinsically rounded).
 */
export function createEllipseDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  sizeOpt: { width: number; height: number } | null,
  options?: CreateEllipseDefaultsOptions,
): EllipseObject {
  const cd = creationDefaults(viewportScale);
  const w = sizeOpt ? sizeOpt.width : cd.rectWidth;
  const h = sizeOpt ? sizeOpt.height : cd.rectHeight;
  const x = sizeOpt ? point.x : point.x - w / 2;
  const y = sizeOpt ? point.y : point.y - h / 2;
  const ellipse: EllipseObject = {
    id: generateShortId(),
    type: 'ellipse',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    strokeColor: options?.strokeColor ?? DEFAULT_STROKE_COLOR,
    strokeWidth: options?.strokeWidth ?? DEFAULT_STROKE_WIDTH,
    fill: options?.fill ?? DEFAULT_FILL,
    ...(options?.boxShadow !== undefined ? { boxShadow: options.boxShadow } : {}),
    ...(options?.backgroundOpacity !== undefined ? { backgroundOpacity: options.backgroundOpacity } : {}),
    ...(options?.strokeStyle !== undefined ? { strokeStyle: options.strokeStyle } : {}),
  };
  return freezeInDev(ellipse);
}
